import {
  ArrowPathIcon,
  Breadcrumb,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  ErrorAlert,
  ExclamationTriangleIcon,
  formatNumber,
  getSmsBalance,
  PageHero,
  smsBalanceStatus,
  useEffect,
  useState,
} from "@";

const Index = () => {
  const links = [{ name: "رصيد نقاط المتجر", link: "" }];

  const [balance, setBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchBalance = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setError(false);
    try {
      const { data } = await getSmsBalance();
      setBalance(data?.amount ?? 0);
      setLastUpdated(new Date());
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchBalance(true);
  };

  const isLow = balance !== null && smsBalanceStatus(balance);
  const showStatus = !isLoading && balance !== null;

  const formatTime = (d) => {
    if (!d) return "";
    return d.toLocaleTimeString("ar-SA", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <ErrorAlert reload={fetchBalance} show={error} />
      <Breadcrumb links={links} />

      <main className="table-card relative pb-4">
        <PageHero
          eyebrow="الرسائل / الرصيد"
          title="النقاط المتبقة"
          icon={ChatBubbleLeftRightIcon}
        >
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
            aria-label="تحديث الرصيد"
            className="group/refresh inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-gray-100 bg-white text-gray-600 transition-all duration-200 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300"
          >
            <ArrowPathIcon
              className={`h-5 w-5 transition-transform duration-500 ${
                isRefreshing
                  ? "animate-spin"
                  : "group-hover/refresh:-rotate-180"
              }`}
            />
          </button>
        </PageHero>

        {/* Balance Card */}
        <div className="relative mx-auto my-8 max-w-2xl">
          {/* Soft outer glow */}
          <span
            aria-hidden
            className="pointer-events-none absolute -inset-4 -z-10 rounded-[2rem] bg-gradient-to-br from-purple-300/30 via-purple-400/20 to-transparent blur-2xl dark:from-purple-700/30 dark:via-purple-900/20"
          />

          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900 via-purple-800 to-purple-950 p-8 shadow-2xl shadow-purple-900/30 ring-1 ring-purple-500/20 dark:from-purple-900 dark:via-purple-950 dark:to-black sm:p-10">
            {/* Decorative dot grid */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.08] [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:24px_24px]"
            />
            {/* Decorative orbs */}
            <span
              aria-hidden
              className="pointer-events-none absolute -end-20 -top-20 h-64 w-64 rounded-full bg-gradient-to-br from-purple-400/40 to-transparent blur-3xl"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -bottom-32 -start-12 h-72 w-72 rounded-full bg-gradient-to-br from-purple-600/30 to-transparent blur-3xl"
            />

            {/* Top row: brand label + status */}
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white/90 ring-1 ring-white/15 backdrop-blur-sm">
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-200/80">
                    رصيد المتجر
                  </p>
                  <p className="text-[13px] font-medium tracking-tight text-white">
                    نقاط الرسائل
                  </p>
                </div>
              </div>

              {showStatus &&
                (isLow ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-400/15 px-3 py-1 text-xs font-bold text-amber-100 ring-1 ring-amber-300/10 backdrop-blur-sm">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    رصيد منخفض
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300/40 bg-green-400/15 px-3 py-1 text-xs font-bold text-green-100 ring-1 ring-green-300/10 backdrop-blur-sm">
                    <CheckBadgeIcon className="h-3.5 w-3.5" />
                    رصيد جيد
                  </span>
                ))}
            </div>

            {/* Big balance number */}
            <div className="relative mt-10 text-center sm:mt-14">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-purple-200/70">
                النقاط المتبقة
              </p>
              <div className="mt-2 flex items-baseline justify-center gap-2">
                {isLoading ? (
                  <span className="block h-16 w-48 animate-pulse rounded-2xl bg-white/10 sm:h-20 sm:w-72" />
                ) : (
                  <>
                    <span
                      className="bg-gradient-to-b from-white to-purple-200 bg-clip-text text-6xl font-black tabular-nums tracking-tighter text-transparent drop-shadow-[0_4px_24px_rgba(255,255,255,0.15)] sm:text-7xl"
                      style={{
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatNumber(balance ?? 0)}
                    </span>
                    <span className="text-lg font-medium text-purple-200/80 sm:text-xl">
                      نقطة
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Bottom row: last updated + dashed divider hint */}
            <div className="relative mt-10 flex items-center justify-between border-t border-white/10 pt-4 sm:mt-14">
              <div className="flex items-center gap-2 text-[12px] text-purple-200/70">
                <span
                  aria-hidden
                  className={`h-1.5 w-1.5 rounded-full ${
                    isLow ? "bg-amber-400" : "bg-green-400"
                  } ${!isLoading ? "animate-pulse" : ""}`}
                />
                {isLoading ? (
                  <span className="block h-3 w-32 animate-pulse rounded bg-white/10" />
                ) : (
                  <span>
                    {lastUpdated ? (
                      <>آخر تحديث: {formatTime(lastUpdated)}</>
                    ) : (
                      "—"
                    )}
                  </span>
                )}
              </div>

              {/* Decorative chip-style hash marks */}
              <div className="hidden items-center gap-1 sm:flex">
                <span className="h-1 w-6 rounded-full bg-white/20" />
                <span className="h-1 w-3 rounded-full bg-white/20" />
                <span className="h-1 w-1.5 rounded-full bg-white/20" />
              </div>
            </div>
          </div>
        </div>

        {/* Helper note */}
        {!isLoading && (
          <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-500">
            في حال انخفاض الرصيد، الرجاء التواصل مع الدعم الفني لشحن نقاط جديدة.
          </p>
        )}
      </main>
    </>
  );
};

export default Index;
